import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import {Component, ElementRef, OnInit, inject, OnDestroy, HostListener, Inject} from '@angular/core';
import {
  DialogService,
  EmbedDbService,
  ProjectService,
  StudioService,
  UserService,
  StyleManagerService
} from '@wms/services';
import { PROJECT_ID_PARAM_NAME, getActionFromKey, getProjectResourcePath } from '@wms/core';
import { SpinnerService } from "@wms/app/services/spinner/spinner.service";
import KeyDownEvent = JQuery.KeyDownEvent;
import {isMobileApp, runtimeEventManager} from "@wm/core";
import {environment, EventManagerService} from "@wms/app";
import { CodeInspectService } from "@wms/bottom-left-menu";
import {MixpanelService} from "@wms/app/services/mixpanel/mixpanel.service";
import {IssuesService} from "@wms/app/bottom-left-menu/components/issues/issues.service";
import { DeploymentApiService } from "@wms/app/deployment/deployment-api/deployment-api.service";
import { Title } from '@angular/platform-browser';
import { filter } from "rxjs";
import { Location } from '@angular/common';
import { WmsWorkspaceManagerService } from "./workspace/services/wms-workspace-manager.service";

let stylePaths = ['./app.component.less'];

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: stylePaths
})
export class AppComponent implements OnInit, OnDestroy {
  private rootEl: JQuery<HTMLElement>;
  showGuidedTour: boolean;
  showGuidedTourSubscription: any;
  appBaseMetaInfo: any;
  baseTitle = 'WaveMaker Studio';

  constructor(private router: Router,
              private activatedRoute: ActivatedRoute,
              private projectService: ProjectService,
              protected spinnerService: SpinnerService,
              private embedDBService: EmbedDbService,
              private userService: UserService,
              protected codeInspectService: CodeInspectService,
              protected issuesService: IssuesService,
              private eventManager: EventManagerService,
              private mixpanelService: MixpanelService,
              private studioService: StudioService,
              private dialogService: DialogService,
              private styleManager: StyleManagerService,
              private deploymentApiService: DeploymentApiService,
              private titleService: Title,
              private location: Location,
              public wsManager: WmsWorkspaceManagerService,
              @Inject("APP_BUILD_META_INFO") appBaseMetaInfo: any) {
    this.updateTitles(this.location.path());
    this.appBaseMetaInfo = appBaseMetaInfo;
    let elRef = inject(ElementRef);
    this.rootEl =  $(elRef.nativeElement);
    const platformType = this.projectService.getProjectPlatformType();
    if (platformType === 'NATIVE_MOBILE') {
      import('./native-mobile/mobile-app.component').then((m) => {
        new m.MobileAppComponent();
      });
    }

    this.eventManager.showSpinnerServiceObservable().subscribe((spinnerMessage: string) => this.spinnerService.show(spinnerMessage));
    this.eventManager.dbLoadedObservable().subscribe(() => this.spinnerService.hide());
    this.showGuidedTourSubscription = this.eventManager.showGuidedTourObservable().subscribe((value: boolean) => this.showGuidedTour = value);
    this.loadStylesByProjectType();
  }
  title = 'ng-studio';
  hideResources: boolean = false;
  projectId: string;
  appQueryParams: any = {};
  runMode: boolean = false; // TO DO
  reloadRequired: any = []; // TO DO
  enableCopilot: boolean = false;

  @HostListener('document:click', ['$event.target'])
  documentClick(target: any) {
    this.eventManager.emitDocumentClick(target);
  }

  // Toggles the visibility of the Co-Pilot feature.
  showCopilot(): void {
    this.enableCopilot = !this.enableCopilot;
  }

  loadStylesByProjectType() {
    // Dynamically load styles depending on the project type.
    let basePath = this.appBaseMetaInfo.CDN_BASE_URL ? this.appBaseMetaInfo.CDN_BASE_URL + 'assets' : 'assets',
      path;

    if (!this.projectService.shouldUseShadowDom()) {
    /* Inject wm-style.css globally if project is not of type prism
    TODO: To be removed later as we only need foundation.css */
      path = basePath + '/styles/runtime-css/wm-style.css';
    } else {
      /* Inject font-paths.css globally to ensure icon font faces and classes are available within shadow DOM for prism projects */
      path = basePath + '/styles/foundation-css/icon-fonts.min.css';
    }
    this.styleManager.addStyle(path);
  }

  addGlobalQueryParams() {
    if (window.location.href.includes(PROJECT_ID_PARAM_NAME + '=')) {
      return;
    }
    this.projectId = this.projectService.getProjectId();
    this.appQueryParams[PROJECT_ID_PARAM_NAME] = this.projectId;

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: this.appQueryParams,
      queryParamsHandling: 'merge'
    })
  }

  ngOnInit() {
    this.addGlobalQueryParams();
     // Expose the toggle function to the console
     (window as any).showCopilot = this.showCopilot.bind(this);

    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) =>{
      this.eventManager.emitOnRouterChange(this.router.url)
      if (event instanceof NavigationEnd) {
      if(this.studioService.getEditInWM())
        this.dialogService.open('EditInWMInfoScreen');
      if (event.url.indexOf('?') === -1) {
        this.addGlobalQueryParams();
      }
    }
    this.updateTitles(this.router.url)
    });

    this.rootEl.on('keydown', (event: KeyDownEvent) => {
      let key = getActionFromKey(event);
      this.eventManager.emitKeyboardAction({key, event});
    });

    (this.deploymentApiService.getEnvType() as Promise<any>).then((envType: string) => {
      if (environment.production && envType === 'WMO') {
        this.loadMixpanelScript();
      }
    });
  }

  loadMixpanelScript() {
    let scriptContent = `(function (f, b) { if (!b.__SV) { var e, g, i, h; window.mixpanel = b; b._i = []; b.init = function (e, f, c) { function g(a, d) { var b = d.split("."); 2 == b.length && ((a = a[b[0]]), (d = b[1])); a[d] = function () { a.push([d].concat(Array.prototype.slice.call(arguments, 0))); }; } var a = b; "undefined" !== typeof c ? (a = b[c] = []) : (c = "mixpanel"); a.people = a.people || []; a.toString = function (a) { var d = "mixpanel"; "mixpanel" !== c && (d += "." + c); a || (d += " (stub)"); return d; }; a.people.toString = function () { return a.toString(1) + ".people (stub)"; }; i = "disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split( " "); for (h = 0; h < i.length; h++) g(a, i[h]); var j = "set set_once union unset remove delete".split(" "); a.get_group = function () { function b(c) { d[c] = function () { call2_args = arguments; call2 = [c].concat(Array.prototype.slice.call(call2_args, 0)); a.push([e, call2]); }; } for ( var d = {}, e = ["get_group"].concat( Array.prototype.slice.call(arguments, 0)), c = 0; c < j.length; c++) b(j[c]); return d; }; b._i.push([e, f, c]); }; b.__SV = 1.2; e = f.createElement("script"); e.type = "text/javascript"; e.async = !0; e.src = "undefined" !== typeof MIXPANEL_CUSTOM_LIB_URL ? MIXPANEL_CUSTOM_LIB_URL : "file:" === f.location.protocol && "//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js".match(/^\\/\\//) ? "https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js" : "//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js"; g = f.getElementsByTagName("script")[0]; g.parentNode.insertBefore(e, g); } })(document, window.mixpanel || []);`;
    var scriptElement = document.createElement('script');
    scriptElement.type = 'text/javascript';
    scriptElement.appendChild(document.createTextNode(scriptContent));
    document.head.appendChild(scriptElement);

    scriptElement.onload = () => {
      // Initialize Mixpanel with your project token after the script is loaded
      this.initMixPanel();
    };
  }

  initMixPanel() {
    const user = this.userService.get() || {}
    const userToken = user.emailAddress;
    const project = this.projectService.getDetails();
    this.mixpanelService.init(userToken, user, project);
  }

  updateTitles(url: string): void {
    const title = this.generateTitleFromUrl(url);
    // Update document title
    this.titleService.setTitle(title)

  }

  generateTitleFromUrl(url: string): string {
    // Remove query parameters and hash if present
    // Title format : [env name -> In case of wmo not need env] pageName/tab Name - project name
    const cleanUrl = url.split('?')[0].split('#')[0].split(';')[0];
    const envNameParts = window.location.host.split('.')[0].split("-");
    const projectDetails = this.projectService.getDetails();

    // Split the URL into segments and remove empty strings
    let segments = cleanUrl.split('/')
      .filter(segment => segment)

    // Handle empty path (home page)
    if (segments.length === 0) {
      return this.baseTitle;
    }
    segments = segments.slice(segments.length-1,segments.length);
    segments.unshift(envNameParts[0]);

    // Convert kebab-case to Title Case and replace hyphens
    const formattedSegments = segments.map(segment => {
      return segment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    });

    // Join all segments with ' - ' and append base title
    if(envNameParts.length < 2) {
      return [formattedSegments[1], projectDetails.name].join(' - ');
    }
    return `[ ${formattedSegments[0].toLowerCase()} ] ${[formattedSegments[1], projectDetails.name].join(' - ')}`;
  }

  ngOnDestroy() {
    runtimeEventManager.off('on-studio-services-initialized')
    if(this.showGuidedTourSubscription)
      this.showGuidedTourSubscription.unsubscribe();
  }
}
